/**
 * Consumables List Page
 * Handles filter panel UI and modals for stock management
 */

import { createDynamicModal } from '../utils/ui/dynamic-modal.js';
import { initConsumableForm } from './consumable-form.js';

const state = {
  urls: {},
  translations: {},
};

let consumableModal = null;
let adjustModal = null;
let setLevelModal = null;

/**
 * Initialize the consumables list page
 */
export function initConsumablesList(options = {}) {
  const { urls = {}, translations = {} } = options;

  state.urls = urls;
  state.translations = translations;

  initFilterPanelUI();
  initConsumableModal();
  initAdjustModal();
  initSetLevelModal();
  setupEventListeners();
  bindConsumableActionsOnce();
}

/**
 * Filter panel toggle / reset / clear-search — delegates data to HTMX
 */
function initFilterPanelUI() {
  const panel = document.getElementById('filter-panel');
  const chevron = document.getElementById('filter-chevron');
  const toggleBtn = document.getElementById('toggle-filters-btn');
  const resetBtn = document.getElementById('reset-filters-btn');
  const clearSearchBtn = document.getElementById('clear-search-btn');
  const form = document.getElementById('stockFilterForm');

  if (toggleBtn && panel && !toggleBtn._csFilterBound) {
    toggleBtn._csFilterBound = true;
    toggleBtn.addEventListener('click', () => {
      const open = panel.classList.toggle('hidden');
      if (chevron) chevron.classList.toggle('rotate-180', !open);
    });
  }

  if (resetBtn && form && !resetBtn._csResetBound) {
    resetBtn._csResetBound = true;
    resetBtn.addEventListener('click', () => {
      const statusSelect = form.querySelector('select[name="status"]');
      const searchInput = document.getElementById('search-input');

      if (statusSelect) statusSelect.value = '';
      if (searchInput) searchInput.value = '';

      form.querySelectorAll('select.form-select').forEach((sel) => {
        const wrapper = sel.closest('.custom-select-wrapper');
        const textEl = wrapper?.querySelector('.custom-select-text');
        if (textEl && sel.options[0]) textEl.textContent = sel.options[0].text;
      });

      if (clearSearchBtn) clearSearchBtn.classList.add('hidden');

      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }

  if (clearSearchBtn && !clearSearchBtn._csClearBound) {
    clearSearchBtn._csClearBound = true;
    clearSearchBtn.addEventListener('click', () => {
      const searchInput = document.getElementById('search-input');
      if (searchInput) searchInput.value = '';
      clearSearchBtn.classList.add('hidden');
      form?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    });
  }
}

// ─── Modals (use the shared dynamic-modal helper) ──────────────────

function buildModalConfig() {
  return {
    onContentInjected: (content) => {
      if (typeof window.initCustomSelects === 'function') {
        window.initCustomSelects();
      }
      // Phase 16 — wire the consumable form (eager-clear validation +
      // single-slot image dropzone). No-op for the adjust / set-level
      // modal bodies which do not contain ``#consumable-form``.
      initConsumableForm(content);
    },
    errorLoadingMessage:
      state.translations.errorLoading || 'Error loading form. Please try again.',
    closeButtonText: state.translations.retry || 'Close',
  };
}

function initConsumableModal() {
  consumableModal = createDynamicModal({ modalId: 'consumable-modal', ...buildModalConfig() });
}

function initAdjustModal() {
  adjustModal = createDynamicModal({ modalId: 'adjust-modal', ...buildModalConfig() });
}

function initSetLevelModal() {
  setLevelModal = createDynamicModal({ modalId: 'setlevel-modal', ...buildModalConfig() });
}

function setupEventListeners() {
  const openBtn = document.getElementById('open-create-modal-btn');
  if (openBtn && !openBtn._csOpenBound) {
    openBtn._csOpenBound = true;
    openBtn.addEventListener('click', () => {
      const url = state.urls.consumableCreate || '/office/stock/consumables/create/';
      const title = state.translations.newConsumable || 'New Consumable';
      consumableModal?.open(url, title);
    });
  }
}

// Internal helpers — invoked by both the row-level action buttons
// (declarative ``data-consumable-action="…"``, Phase 21.A) and the
// page-level "+" toolbar button. No ``window.*`` exposure: the only
// callers were the inline ``onclick="openXxxModal(...)"`` handlers
// in ``stock/consumable_list.html``, both retired in Phase 21.A.

function openEditModal(consumableId, consumableName) {
  const url = `/office/stock/consumables/${consumableId}/edit/`;
  const title = (state.translations.edit || 'Edit') + ': ' + consumableName;
  consumableModal?.open(url, title);
}

function openAdjustModal(consumableId, consumableName) {
  const url = `/office/stock/consumables/${consumableId}/adjust/`;
  const title = (state.translations.adjustStock || 'Adjust Stock') + ': ' + consumableName;
  adjustModal?.open(url, title);
}

function openSetLevelModal(consumableId, consumableName) {
  const url = `/office/stock/consumables/${consumableId}/set-level/`;
  const title = (state.translations.setLevel || 'Set Stock Level') + ': ' + consumableName;
  setLevelModal?.open(url, title);
}

// Delegate at document level for the row-level action buttons on
// the consumables list. Phase 21.A migration of the three inline
// ``onclick="openXxxModal(id, name)"`` triggers. Idempotent —
// bound once per SPA lifetime via ``_csActionsBound``.
//
// Bound in the CAPTURE phase so the open-edit button (which sits
// in the action cell with no ``data-stop-propagation`` parent)
// AND the open-adjust / open-set-level buttons (which carry their
// own ``data-stop-propagation`` to keep the embedded toggle form
// from triggering on tile click) all reach this delegate before
// any stopper fires. Same pattern as ``utils/ui/confirm-action.js``.
let _csActionsBound = false;

const CONSUMABLE_ACTIONS = {
  'open-edit': openEditModal,
  'open-adjust': openAdjustModal,
  'open-set-level': openSetLevelModal,
};

function bindConsumableActionsOnce() {
  if (_csActionsBound) return;
  _csActionsBound = true;

  document.body.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-consumable-action]');
    if (!trigger) return;
    const fn = CONSUMABLE_ACTIONS[trigger.dataset.consumableAction];
    if (typeof fn !== 'function') return;
    const id = parseInt(trigger.dataset.consumableId, 10);
    if (!Number.isFinite(id)) return;
    const name = trigger.dataset.consumableName || '';
    fn(id, name);
  }, true);
}

let afterSettleBound = false;

function bindAfterSettleOnce() {
  if (afterSettleBound) return;
  afterSettleBound = true;
  document.body.addEventListener('htmx:afterSettle', () => {
    if (typeof window.reinitFlowbiteWidgets === 'function') {
      window.reinitFlowbiteWidgets();
    }
  });
}

/**
 * Auto-init entry point (wired via ``data-auto-init="consumablesList"``).
 */
export function autoInitConsumablesList() {
  const root = document.querySelector('[data-auto-init="consumablesList"]');
  if (!root) return;
  const ds = root.dataset;
  initConsumablesList({
    urls: {
      consumableCreate: ds.consumableCreateUrl || '/office/stock/consumables/create/',
    },
    translations: {
      newConsumable: ds.trNewConsumable || 'New Consumable',
      edit: ds.trEdit || 'Edit',
      adjustStock: ds.trAdjustStock || 'Adjust Stock',
      setLevel: ds.trSetLevel || 'Set Stock Level',
      errorLoading: ds.trErrorLoading || 'Error loading form. Please try again.',
      retry: ds.trRetry || 'Retry',
    },
  });
  bindAfterSettleOnce();
}

window.initConsumablesList = initConsumablesList;
